/**
 * ONE business-area control: the switcher in the top bar. Pages never keep their own area state.
 *
 * Pages that can only show one area at a time (Strategy, Review, Products, Selling rules, AI reports) call
 * `useOneArea()`. If the switcher is on "All business areas" the hook moves the switcher itself to one area
 * (the last one used, else the first), so the top bar and the page always say the same thing.
 */
import { useEffect, useMemo } from 'react';
import { useSession } from './session.js';
import { areaLabel } from './plain.js';

export interface OneArea { id: string; key: string; label: string; role: string }
const LAST = 'bs.lastArea';

export function rememberArea(key: string): void {
  try { localStorage.setItem(LAST, key); } catch (e) { console.warn('localStorage unavailable', e); }
}
function lastArea(): string | null {
  try { return localStorage.getItem(LAST); } catch (e) { console.warn('localStorage unavailable', e); return null; }
}

/** Pure choice, exported for tests: which area a one-area page shows. */
export function pickOneArea<T extends { key: string }>(switcher: string, areas: T[], last: string | null): T | null {
  if (!areas.length) return null;
  if (switcher !== 'all') return areas.find((a) => a.key === switcher) ?? areas[0]!;
  return areas.find((a) => a.key === last) ?? areas[0]!;
}

export function useOneArea(): { area: OneArea | null; areas: OneArea[]; isAdmin: boolean; isAreaAdmin: boolean } {
  const { me, vertical, setVertical } = useSession();
  const areas = useMemo<OneArea[]>(() => (me?.memberships ?? []).map((m) => ({ id: m.verticalId, key: m.verticalKey, label: areaLabel(m.verticalKey), role: m.role })), [me]);
  const area = pickOneArea(vertical, areas, lastArea());
  useEffect(() => {
    if (area && vertical === 'all') setVertical(area.key);
  }, [area, vertical, setVertical]);
  const isAdmin = !!me?.isAdmin;
  return { area, areas, isAdmin, isAreaAdmin: !!area && (isAdmin || area.role === 'admin') };
}

/** Shown where a view needs one area while the top bar says "All": the buttons move the top-bar switcher. */
export function ChooseArea({ what }: { what: string }) {
  const { me, setVertical } = useSession();
  const ms = me?.memberships ?? [];
  return (
    <div className="rounded-lg border border-dashed bg-card p-8 text-center">
      <p className="text-[15px] font-semibold">Choose one business area</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{what} is written for one business area at a time. Pick one here or in the switcher at the top.</p>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        {ms.map((m) => (
          <button key={m.verticalId} type="button" onClick={() => setVertical(m.verticalKey)}
            className="rounded-md border bg-background px-4 py-2 text-sm font-medium shadow-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {areaLabel(m.verticalKey)}
          </button>
        ))}
      </div>
    </div>
  );
}
